import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useMyConnections, CommunityConnection, ConnectionProfile } from "@/hooks/useCommunityConnections";
import { useAuth } from "@/lib/auth";
import { getStorageUrl } from "@/lib/storage";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { UserName } from "@/components/badges/UserName";

interface ConnectionsPreviewProps {
  onViewProfile?: (userId: string) => void;
}

export function ConnectionsPreview({ onViewProfile }: ConnectionsPreviewProps) {
  const { user } = useAuth();
  const { data: connections, isLoading } = useMyConnections();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(!isMobile);

  // Update open state when mobile status changes
  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  // Get the connected person's profile from a connection
  const getConnectedPerson = (connection: CommunityConnection): ConnectionProfile | null => {
    if (!user?.id) return null;
    if (connection.requester_id === user.id) {
      return connection.recipient || null;
    }
    return connection.requester || null;
  };

  // Get the 6 most recent connections
  const recentConnections = connections?.slice(0, 6) || [];

  if (isLoading) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-20 w-20 flex-shrink-0 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="p-0 h-auto hover:bg-transparent">
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                  <CardTitle className="text-base font-semibold">
                    My Connections ({connections?.length || 0})
                  </CardTitle>
                </div>
              </Button>
            </CollapsibleTrigger>
            <Button variant="link" size="sm" asChild className="text-primary">
              <Link to="/connections">View All Connections →</Link>
            </Button>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {recentConnections.length === 0 ? (
              <div className="flex items-center gap-3 py-4 text-muted-foreground">
                <Users className="h-5 w-5" />
                <span className="text-sm">You haven't connected with anyone yet</span>
              </div>
            ) : (
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex gap-4 pb-2">
                  {recentConnections.map((connection) => {
                    const person = getConnectedPerson(connection);
                    if (!person) return null;

                    return (
                      <button
                        key={connection.id}
                        onClick={() => onViewProfile?.(person.id)}
                        className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 transition-colors min-w-[100px] text-center"
                      >
                        <Avatar className="h-14 w-14">
                          <AvatarImage src={getStorageUrl("avatars", person.avatar_url) || undefined} />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {person.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1">
                          <p className="text-sm font-medium truncate max-w-[80px]">
                            <UserName userId={person.id} name={person.full_name?.split(" ")[0]} />
                          </p>
                          <Badge
                            variant={person.role === "mentor" ? "default" : "secondary"}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {person.role}
                          </Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
